"use client";

import { useEffect, useRef, useState } from "react";
import { ScanLine, Sparkles, Upload } from "lucide-react";
import { screenInvoiceAction, type InvoiceScreenerResult } from "@/app/actions";

const CAPTIONS = [
  "Reading the invoice…",
  "Finding the vendor…",
  "Checking the bank details…",
  "Double-checking the numbers…",
];

/**
 * Alternate entry point into the payout wizard: skip the manual amount/recipient
 * form entirely and let Claude read an uploaded invoice, then hand the result
 * straight to PayoutForm as `initial` + `prefill`.
 */
export default function InvoiceScreener({ onScreened }: { onScreened: (result: InvoiceScreenerResult) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captionIdx, setCaptionIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!pending) {
      setCaptionIdx(0);
      return;
    }
    const id = setInterval(() => setCaptionIdx((i) => (i + 1) % CAPTIONS.length), 1400);
    return () => clearInterval(id);
  }, [pending]);

  function reset() {
    setFile(null);
    setError(null);
  }

  async function onFile(f: File | undefined | null) {
    if (!f) return;
    setFile(f);
    setError(null);
    setPending(true);
    const fd = new FormData();
    fd.set("file", f);
    const res = await screenInvoiceAction(fd);
    setPending(false);
    if (res.ok) onScreened(res.data);
    else setError(res.error);
  }

  const isImage = file?.type.startsWith("image/");
  const isPdf = file?.type === "application/pdf";

  return (
    <div className="card rise h-full p-8">
      <div className="mb-1 flex items-center gap-2">
        <ScanLine size={18} />
        <h2 className="text-[17px] font-medium">Invoice Screener</h2>
        <Sparkles size={14} style={{ color: "var(--c-green)" }} />
      </div>
      <p className="mb-5 text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
        Upload an invoice — Claude reads the vendor, bank details, amount and currency, and prefills this payout for
        you.
      </p>

      {!file ? (
        <label
          className="flex cursor-pointer flex-col items-center gap-2 rounded-[16px] border-2 border-dashed p-10 text-center transition-colors duration-150 hover:bg-[var(--c-cream)]"
          style={{ borderColor: "var(--c-hairline)" }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            onFile(e.dataTransfer.files?.[0]);
          }}
        >
          <Upload size={28} style={{ color: "var(--c-ink-soft)" }} />
          <span className="text-[14px] font-medium">Drop an invoice here, or click to upload</span>
          <span className="text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
            PDF, JPG, PNG, GIF or WEBP
          </span>
          <input
            ref={inputRef}
            type="file"
            hidden
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </label>
      ) : (
        <div>
          <div
            className={`relative overflow-hidden rounded-[16px] ${pending ? "scan-frame" : ""}`}
            style={{ height: 320, background: "var(--c-charcoal)" }}
          >
            {isImage && previewUrl && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={previewUrl} alt="Uploaded invoice" className="h-full w-full object-contain" />
            )}
            {isPdf && previewUrl && (
              <embed src={`${previewUrl}#toolbar=0&navpanes=0`} type="application/pdf" className="h-full w-full" />
            )}
            {pending && (
              <>
                <div className="scan-beam" />
                <div className="scan-corner scan-corner-tl" />
                <div className="scan-corner scan-corner-tr" />
                <div className="scan-corner scan-corner-bl" />
                <div className="scan-corner scan-corner-br" />
              </>
            )}
          </div>

          {pending ? (
            <p className="mt-3 text-center text-[13px] font-medium" style={{ color: "var(--c-ink-soft)" }}>
              {CAPTIONS[captionIdx]}
            </p>
          ) : (
            error && (
              <div className="card-cream mt-4 p-4 text-[14px] font-light" style={{ color: "var(--c-danger)" }}>
                {error}
                <div>
                  <button type="button" className="btn btn-ghost mt-3 !px-4 !py-1.5 text-[13px]" onClick={reset}>
                    Try another file
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
